import { Badge, Group, Paper, Select, Stack, Switch, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { useAuthStore } from '../stores/auth-store';
import { apiHook } from '../treaty';

export function UsersPage() {
  const currentUser = useAuthStore((state) => state.user)!;
  const queryClient = useQueryClient();
  const { data: users, isLoading } = apiHook.useQuery((client) => client.admin.users.get());

  async function updateUser(id: string, update: { role?: 'admin' | 'member'; disabled?: boolean }) {
    const response = await api.api.api.v1.admin.users({ id }).patch(update);
    if (response.error) {
      notifications.show({ color: 'red', message: 'The account could not be updated.' });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    notifications.show({
      color: 'green',
      message: 'Account updated. Existing sessions were revoked.',
    });
  }

  return (
    <Stack gap='lg' maw={1100} mx='auto'>
      <div>
        <Group justify='space-between' align='end'>
          <div>
            <Title order={1}>Team users</Title>
            <Text c='dimmed' mt={5}>
              Manage access and roles for accounts created on the server.
            </Text>
          </div>
          <Badge variant='light' size='lg'>
            {users?.length ?? 0} accounts
          </Badge>
        </Group>
      </div>
      <Paper withBorder radius='lg' className='data-card'>
        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing='md' horizontalSpacing='lg' highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta='right'>Access</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users?.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Text fw={600}>{user.email}</Text>
                    {user.id === currentUser.id && (
                      <Text size='xs' c='blue'>
                        Current account
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Select
                      aria-label={`Role for ${user.email}`}
                      value={user.role}
                      data={[
                        { value: 'member', label: 'Member' },
                        { value: 'admin', label: 'Administrator' },
                      ]}
                      disabled={user.id === currentUser.id}
                      w={165}
                      onChange={(value) =>
                        value && updateUser(user.id, { role: value as 'admin' | 'member' })
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <Badge color={user.disabled ? 'gray' : 'green'} variant='light'>
                      {user.disabled ? 'Disabled' : 'Active'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group justify='flex-end'>
                      <Switch
                        aria-label={`Access for ${user.email}`}
                        checked={!user.disabled}
                        disabled={user.id === currentUser.id}
                        onChange={(event) =>
                          updateUser(user.id, { disabled: !event.currentTarget.checked })
                        }
                        label='Can sign in'
                      />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && users?.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c='dimmed' ta='center'>
                      No users found.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      <Paper withBorder radius='lg' p='lg' bg='var(--mantine-color-blue-0)'>
        <Text fw={600}>Create accounts from the server</Text>
        <Text size='sm' c='dimmed' mt={4}>
          Run <code>bun run user:create email@example.com password member</code>. Use{' '}
          <code>admin</code> as the final value for an administrator.
        </Text>
      </Paper>
    </Stack>
  );
}
